import React, { useState } from 'react';
import { StatsCard } from './shared/StatsCard';
import { Chart } from './shared/Chart';
import { Download, DollarSign, Droplet, Fish, Wallet, AlertCircle, ExternalLink, ArrowUpRight, Brain, Repeat, Zap, Globe, Theater, Camera, Heart, Gamepad2, Pill, Info, BarChart } from 'lucide-react';
// User segments
const segments = [{
  id: 'whales',
  label: 'Whales',
  icon: <DollarSign size={16} className="text-emerald-500" data-id="element-10556" />
}, {
  id: 'dolphins',
  label: 'Dolphins',
  icon: <Droplet size={16} className="text-blue-500" data-id="element-10557" />
}, {
  id: 'minnows',
  label: 'Minnows',
  icon: <Fish size={16} className="text-cyan-500" data-id="element-10558" />
}, {
  id: 'non-spenders',
  label: 'Non-Spenders',
  icon: <Wallet size={16} className="text-gray-500" data-id="element-10559" />
}, {
  id: 'churn-risks',
  label: 'Churn Risks',
  icon: <AlertCircle size={16} className="text-red-500" data-id="element-10560" />
}];
// External verticals
const externalVerticals = [{
  id: 'cams',
  name: 'Live Cams',
  icon: <Camera size={16} className="text-pink-500" data-id="element-10561" />,
  revenue: {
    whales: 85000,
    dolphins: 65000,
    minnows: 35000,
    'non-spenders': 15000,
    'churn-risks': 45000
  },
  algorithmCoverage: 85,
  fallbackMethod: 'lookalike',
  smartBanners: 3
}, {
  id: 'dating',
  name: 'Dating Sites',
  icon: <Heart size={16} className="text-red-500" data-id="element-10562" />,
  revenue: {
    whales: 55000,
    dolphins: 95000,
    minnows: 85000,
    'non-spenders': 45000,
    'churn-risks': 40000
  },
  algorithmCoverage: 78,
  fallbackMethod: 'context',
  smartBanners: 4
}, {
  id: 'games',
  name: 'Adult Games',
  icon: <Gamepad2 size={16} className="text-purple-500" data-id="element-10563" />,
  revenue: {
    whales: 35000,
    dolphins: 45000,
    minnows: 65000,
    'non-spenders': 25000,
    'churn-risks': 30000
  },
  algorithmCoverage: 65,
  fallbackMethod: 'persona',
  smartBanners: 2
}, {
  id: 'nutra',
  name: 'Nutra Supplements',
  icon: <Pill size={16} className="text-green-500" data-id="element-10564" />,
  revenue: {
    whales: 65000,
    dolphins: 75000,
    minnows: 45000,
    'non-spenders': 5000,
    'churn-risks': 25000
  },
  algorithmCoverage: 72,
  fallbackMethod: 'broad',
  smartBanners: 3
}];
// Fallback methods
const fallbackMethods = [{
  id: 'lookalike',
  label: 'Lookalike Audiences',
  icon: <Repeat size={16} className="text-indigo-500" data-id="element-10565" />
}, {
  id: 'broad',
  label: 'Broad Targeting',
  icon: <Zap size={16} className="text-amber-500" data-id="element-10566" />
}, {
  id: 'context',
  label: 'Context Fallback',
  icon: <Globe size={16} className="text-blue-500" data-id="element-10567" />
}, {
  id: 'persona',
  label: 'Persona Fallback',
  icon: <Theater size={16} className="text-pink-500" data-id="element-10568" />
}];
// Smart banners
const smartBanners = [{
  id: 'banner1',
  name: 'Live Cam Teaser',
  vertical: 'cams',
  image: 'https://images.unsplash.com/photo-1620912189865-1e8a33da4c5e?q=80&w=2069&auto=format&fit=crop',
  title: 'Live Shows Starting Now',
  description: 'Join live shows with your favorite performers',
  ctaText: 'Watch Now',
  targetSegment: 'whales',
  algorithmCoverage: 92,
  fallbackMethod: 'lookalike',
  impressions: 125000,
  clicks: 18750,
  conversions: 1875,
  revenue: 84375
}, {
  id: 'banner2',
  name: 'Dating Offer',
  vertical: 'dating',
  image: 'https://images.unsplash.com/photo-1516726817505-f5ed825624d8?q=80&w=2069&auto=format&fit=crop',
  title: 'Find Local Matches',
  description: 'Connect with real people in your area',
  ctaText: 'Start Matching',
  targetSegment: 'minnows',
  algorithmCoverage: 85,
  fallbackMethod: 'context',
  impressions: 250000,
  clicks: 32500,
  conversions: 3250,
  revenue: 113750
}, {
  id: 'banner3',
  name: 'Supplement Offer',
  vertical: 'nutra',
  image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070&auto=format&fit=crop',
  title: 'Boost Your Performance',
  description: 'Try our premium supplement for enhanced results',
  ctaText: 'Claim Offer',
  targetSegment: 'dolphins',
  algorithmCoverage: 88,
  fallbackMethod: 'broad',
  impressions: 180000,
  clicks: 21600,
  conversions: 2160,
  revenue: 118800
}, {
  id: 'banner4',
  name: 'Game Promotion',
  vertical: 'games',
  image: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=2071&auto=format&fit=crop',
  title: 'Fantasy Simulator',
  description: 'Create your dream scenarios in our immersive game',
  ctaText: 'Play Now',
  targetSegment: 'non-spenders',
  algorithmCoverage: 75,
  fallbackMethod: 'persona',
  impressions: 320000,
  clicks: 28800,
  conversions: 2880,
  revenue: 72000
}];
// Chart data for revenue by segment per vertical
const revenueBySegmentData = [{
  name: 'Whales',
  cams: externalVerticals.find(v => v.id === 'cams')?.revenue.whales || 0,
  dating: externalVerticals.find(v => v.id === 'dating')?.revenue.whales || 0,
  games: externalVerticals.find(v => v.id === 'games')?.revenue.whales || 0,
  nutra: externalVerticals.find(v => v.id === 'nutra')?.revenue.whales || 0
}, {
  name: 'Dolphins',
  cams: externalVerticals.find(v => v.id === 'cams')?.revenue.dolphins || 0,
  dating: externalVerticals.find(v => v.id === 'dating')?.revenue.dolphins || 0,
  games: externalVerticals.find(v => v.id === 'games')?.revenue.dolphins || 0,
  nutra: externalVerticals.find(v => v.id === 'nutra')?.revenue.dolphins || 0
}, {
  name: 'Minnows',
  cams: externalVerticals.find(v => v.id === 'cams')?.revenue.minnows || 0,
  dating: externalVerticals.find(v => v.id === 'dating')?.revenue.minnows || 0,
  games: externalVerticals.find(v => v.id === 'games')?.revenue.minnows || 0,
  nutra: externalVerticals.find(v => v.id === 'nutra')?.revenue.minnows || 0
}];
// Chart data for banner performance
const bannerPerformanceData = smartBanners.map(banner => ({
  name: banner.name,
  impressions: banner.impressions,
  clicks: banner.clicks,
  conversions: banner.conversions
}));
export const CrossVertical: React.FC = () => {
  const [selectedBanner, setSelectedBanner] = useState<string | null>(null);
  const [bannerFallbacks, setBannerFallbacks] = useState(smartBanners.reduce((acc, banner) => ({
    ...acc,
    [banner.id]: banner.fallbackMethod
  }), {}));
  const handleFallbackMethodChange = (bannerId: string, method: string) => {
    setBannerFallbacks({
      ...bannerFallbacks,
      [bannerId]: method
    });
  };
  const totalRevenue = externalVerticals.reduce((sum, vertical) => {
    return sum + Object.values(vertical.revenue).reduce((verticalSum, revenue) => verticalSum + revenue, 0);
  }, 0);
  const totalBannerRevenue = smartBanners.reduce((sum, banner) => sum + banner.revenue, 0);
  const totalBannerImpressions = smartBanners.reduce((sum, banner) => sum + banner.impressions, 0);
  const totalBannerClicks = smartBanners.reduce((sum, banner) => sum + banner.clicks, 0);
  const totalBannerConversions = smartBanners.reduce((sum, banner) => sum + banner.conversions, 0);
  const avgAlgorithmCoverage = externalVerticals.reduce((sum, vertical) => sum + vertical.algorithmCoverage, 0) / externalVerticals.length;
  return <div data-id="element-10569">
      <div className="flex items-center justify-between mb-6" data-id="element-10570">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-10571">
          Cross-Vertical Synergy
        </h2>
        <div className="flex items-center gap-3" data-id="element-10572">
          <button className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-10573">
            <Download size={16} data-id="element-10574" />
            <span data-id="element-10575">Export Data</span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-10576">
        <StatsCard title="Total External Revenue" value={`$${(totalRevenue / 1000).toFixed(1)}K`} change={15.3} changeLabel="vs last month" icon={<ExternalLink size={16} className="text-blue-500" data-id="element-10578" />} className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/20" data-id="element-10577" />
        <StatsCard title="Banner Revenue" value={`$${(totalBannerRevenue / 1000).toFixed(1)}K`} change={18.7} changeLabel="vs last month" icon={<ArrowUpRight size={16} className="text-green-500" data-id="element-10580" />} className="bg-gradient-to-br from-white to-green-50 dark:from-gray-800 dark:to-green-900/20" data-id="element-10579" />
        <StatsCard title="Banner Conversions" value={totalBannerConversions.toLocaleString()} change={12.2} changeLabel="vs last month" icon={<BarChart size={16} className="text-amber-500" data-id="element-10582" />} data-id="element-10581" />
        <StatsCard title="Algorithm Coverage" value={`${avgAlgorithmCoverage.toFixed(1)}%`} change={4.5} changeLabel="vs last month" icon={<Brain size={16} className="text-purple-500" data-id="element-10584" />} className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20" data-id="element-10583" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-10585">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-10586">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10587">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10588">
              <BarChart size={18} className="text-blue-500 mr-2" data-id="element-10589" />
              Revenue by Segment & Vertical
            </h3>
          </div>
          <div className="p-6" data-id="element-10590">
            <div className="h-80" data-id="element-10591">
              <Chart title="" data={revenueBySegmentData} type="bar" height={300} yAxisLabel="Revenue ($)" data-id="element-10592" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-10593">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10594">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10595">
              <ArrowUpRight size={18} className="text-green-500 mr-2" data-id="element-10596" />
              Banner Performance
            </h3>
          </div>
          <div className="p-6" data-id="element-10597">
            <div className="h-80" data-id="element-10598">
              <Chart title="" data={bannerPerformanceData} type="bar" height={300} yAxisLabel="Count" data-id="element-10599" />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-10600">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10601">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10602">
            <ExternalLink size={18} className="text-blue-500 mr-2" data-id="element-10603" />
            External Verticals Performance
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-10604">
          <table className="w-full" data-id="element-10605">
            <thead data-id="element-10606">
              <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-10607">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10608">
                  Vertical
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10609">
                  Whales Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10610">
                  Dolphins Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10611">
                  Minnows Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10612">
                  Non-Spenders Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10613">
                  Churn Risks Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10614">
                  Algorithm Coverage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10615">
                  Fallback Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10616">
                  Smart Banners
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-10617">
              {externalVerticals.map(vertical => <tr key={vertical.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-10618">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10619">
                    <div className="flex items-center" data-id="element-10620">
                      <div className="flex-shrink-0" data-id="element-10621">
                        <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-10622">
                          {vertical.icon}
                        </div>
                      </div>
                      <div className="ml-3" data-id="element-10623">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-10624">
                          {vertical.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10625">
                    ${vertical.revenue.whales.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10626">
                    ${vertical.revenue.dolphins.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10627">
                    ${vertical.revenue.minnows.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10628">
                    ${vertical.revenue['non-spenders'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10629">
                    ${vertical.revenue['churn-risks'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10630">
                    <div className="flex items-center" data-id="element-10631">
                      <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-10632">
                        <div className="h-full bg-purple-500 rounded-full" style={{
                      width: `${vertical.algorithmCoverage}%`
                    }} data-id="element-10633"></div>
                      </div>
                      <span className="ml-2" data-id="element-10634">
                        {vertical.algorithmCoverage}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10635">
                    <div className="flex items-center" data-id="element-10636">
                      {fallbackMethods.find(m => m.id === vertical.fallbackMethod)?.icon}
                      <span className="ml-2" data-id="element-10637">
                        {fallbackMethods.find(m => m.id === vertical.fallbackMethod)?.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10638">
                    {vertical.smartBanners}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-10639">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10640">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10641">
            <Info size={18} className="text-blue-500 mr-2" data-id="element-10642" />
            Cross-Vertical Targeting Strategy
          </h3>
        </div>
        <div className="p-6" data-id="element-10643">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10644">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4" data-id="element-10645">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-10646">
                <Brain size={16} className="text-blue-500 mr-2" data-id="element-10647" />
                Algorithmic Cross-Vertical Targeting
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-10648">
                The system analyzes these signals to determine optimal external
                vertical targeting:
              </p>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 list-disc pl-5" data-id="element-10649">
                <li data-id="element-10650">Chat content analysis (interests, desires, needs)</li>
                <li data-id="element-10651">Previous external link engagement patterns</li>
                <li data-id="element-10652">Similar user conversion data across verticals</li>
                <li data-id="element-10653">Spending behavior and price sensitivity</li>
                <li data-id="element-10654">Time-based engagement patterns</li>
              </ul>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4" data-id="element-10655">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-10656">
                <Repeat size={16} className="text-purple-500 mr-2" data-id="element-10657" />
                Fallback Hierarchy
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-10658">
                When algorithm confidence is below threshold, the system follows
                this fallback order:
              </p>
              <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-10659">
                <li className="flex items-start" data-id="element-10660">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5 mr-2" data-id="element-10661">
                    1
                  </div>
                  <div data-id="element-10662">
                    <strong data-id="element-10663">Lookalike Audiences:</strong> Target users similar
                    to known converters in that vertical
                  </div>
                </li>
                <li className="flex items-start" data-id="element-10664">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5 mr-2" data-id="element-10665">
                    2
                  </div>
                  <div data-id="element-10666">
                    <strong data-id="element-10667">Broad Targeting:</strong> Target based on general
                    segment performance in that vertical
                  </div>
                </li>
                <li className="flex items-start" data-id="element-10668">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5 mr-2" data-id="element-10669">
                    3
                  </div>
                  <div data-id="element-10670">
                    <strong data-id="element-10671">Context Fallback:</strong> Target based on device,
                    location, time of day
                  </div>
                </li>
                <li className="flex items-start" data-id="element-10672">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-pink-100 dark:bg-pink-900/50 text-pink-600 dark:text-pink-400 flex-shrink-0 mt-0.5 mr-2" data-id="element-10673">
                    4
                  </div>
                  <div data-id="element-10674">
                    <strong data-id="element-10675">Persona Fallback:</strong> Last resort using basic
                    demographic personas
                  </div>
                </li>
              </ol>
            </div>
          </div>
          <div className="mt-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg" data-id="element-10676">
            <div className="flex items-start" data-id="element-10677">
              <div className="flex-shrink-0 mt-1" data-id="element-10678">
                <BarChart size={18} className="text-green-500" data-id="element-10679" />
              </div>
              <div className="ml-3" data-id="element-10680">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-10681">
                  Segment-Specific Vertical Performance
                </h4>
                <div className="mt-2 space-y-2" data-id="element-10682">
                  <div className="flex items-start" data-id="element-10683">
                    <DollarSign size={14} className="text-emerald-500 mr-2 flex-shrink-0 mt-1" data-id="element-10684" />
                    <div data-id="element-10685">
                      <strong className="text-sm text-gray-900 dark:text-white" data-id="element-10686">
                        Whales:
                      </strong>
                      <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-10687">
                        Highest conversion on Cams (15.2% CVR) and Nutra (12.8%
                        CVR). Algorithm coverage: 92%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-10688">
                    <Droplet size={14} className="text-blue-500 mr-2 flex-shrink-0 mt-1" data-id="element-10689" />
                    <div data-id="element-10690">
                      <strong className="text-sm text-gray-900 dark:text-white" data-id="element-10691">
                        Dolphins:
                      </strong>
                      <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-10692">
                        Best performance on Dating (10.5% CVR) and Nutra (9.2%
                        CVR). Algorithm coverage: 85%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-10693">
                    <Fish size={14} className="text-cyan-500 mr-2 flex-shrink-0 mt-1" data-id="element-10694" />
                    <div data-id="element-10695">
                      <strong className="text-sm text-gray-900 dark:text-white" data-id="element-10696">
                        Minnows:
                      </strong>
                      <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-10697">
                        Strong on Dating (8.5% CVR) and Games (7.2% CVR).
                        Algorithm coverage: 72%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-10698">
                    <Wallet size={14} className="text-gray-500 mr-2 flex-shrink-0 mt-1" data-id="element-10699" />
                    <div data-id="element-10700">
                      <strong className="text-sm text-gray-900 dark:text-white" data-id="element-10701">
                        Non-Spenders:
                      </strong>
                      <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-10702">
                        Best on Dating (3.2% CVR) and Games (2.8% CVR).
                        Algorithm coverage: 45%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-10703">
                    <AlertCircle size={14} className="text-red-500 mr-2 flex-shrink-0 mt-1" data-id="element-10704" />
                    <div data-id="element-10705">
                      <strong className="text-sm text-gray-900 dark:text-white" data-id="element-10706">
                        Churn Risks:
                      </strong>
                      <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-10707">
                        Highest recovery through Cams (9.5% CVR) and Dating
                        (8.2% CVR). Algorithm coverage: 88%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};